// englishcontent6.js — Section 6: Spoken Language
// Expanded Grade 9 Ready content:
// - More precise GCSE/KS3-KS4 alignment
// - Richer explainers
// - 3 interactive questions per topic
// - More practice questions
// - Stronger worked examples
// - Clearer assessment/AI feedback criteria

const makeTopic = (topic) => ({
  duration: '25-35 mins',
  xp: 100,
  difficulty: 'foundation',
  ...topic,
});

const tier = (label, text, critique) => ({ label, text, critique });

export const englishContent6 = {
  id: 6,
  title: 'Spoken Language',
  slug: 'spoken-language',
  color: 'rose',
  icon: 'Mic',
  description:
    'Speaking skills — formal speeches, presentations, discussion, debate, performance and confident spoken Standard English.',
  topics: [

    // =========================================================
    // TOPIC 1 — SPOKEN LANGUAGE TASKS
    // =========================================================
    makeTopic({
      id: 'eng-6-1',
      slug: 'spoken-language-tasks',
      title: 'Spoken Language Tasks',
      summary: 'Understand what spoken language tasks involve and how to succeed in them.',

      explainer: {
        title: 'Spoken Language Tasks',
        content: `
Spoken language tasks assess how clearly, confidently and appropriately you can communicate ideas to an audience.

You may be asked to complete:
- A formal speech or presentation.
- A persuasive talk on an issue.
- A group discussion or debate.
- A role-play, interview or structured conversation.
- A performance of a poem, play script or dramatic extract.
- Follow-up questions from your teacher, examiner or classmates.

Most spoken tasks assess four main areas:

1. CONTENT
This means what you say.
Strong content is:
- Clear
- Relevant
- Developed
- Supported with examples, evidence or detail

2. ORGANISATION
This means how your ideas are structured.
A strong spoken response usually has:
- A clear opening
- Linked points
- Signposting phrases
- A controlled ending

3. LANGUAGE
This means the words and style you choose.
In formal assessed speaking, you should usually:
- Use Standard English
- Avoid slang and unnecessary filler
- Choose precise vocabulary
- Adapt your tone to the audience and purpose

4. DELIVERY AND RESPONSE
This means how you speak and how you respond to others.
Strong delivery includes:
- Clear volume
- Controlled pace
- Pauses for emphasis
- Eye contact
- Confident posture
- Listening carefully
- Answering questions thoughtfully

A spoken task is not only about reading words aloud. It is about controlling meaning, tone and audience impact.

Best preparation method:
- Plan your key ideas.
- Practise aloud.
- Time yourself.
- Record yourself.
- Improve one thing at a time: pace, clarity, pauses, vocabulary or structure.

A confident speaker is not someone who never feels nervous. A confident speaker is someone who has prepared enough to keep control even when they feel nervous.
`
      },

      interactiveLesson: {
        title: 'Spoken Tasks',
        steps: [
          {
            type: 'question',
            question: 'Which of these is most likely to be assessed in a spoken language task?',
            options: ['Clear communication to an audience', 'Handwriting style', 'How fast you can read silently'],
            correct: 0
          },
          {
            type: 'question',
            question: 'What does CONTENT mean in a spoken task?',
            options: ['What you say and how well your ideas are developed', 'The colour of your slides', 'How loudly the audience claps'],
            correct: 0
          },
          {
            type: 'question',
            question: 'Which preparation method is most useful?',
            options: ['Practise aloud and record yourself', 'Only read the notes once', 'Memorise every word without understanding it'],
            correct: 0
          }
        ]
      },

      workedExample: {
        title: 'Worked Example — Planning a 5-minute Speech',
        content: `
Task: Prepare a 5-minute speech on "Why reading matters".

Weak plan:
- Reading is good.
- It helps people.
- I like books.
- Thank you.

This is too vague. It has no clear structure, evidence or audience impact.

Improved plan:
1. Hook:
"Imagine living an entire life with only one point of view. That is what we lose when we stop reading."

2. Main point:
Reading matters because it develops empathy, vocabulary and independent thinking.

3. Point 1 — Empathy:
Stories allow us to experience lives different from our own.

4. Point 2 — Vocabulary:
Regular reading exposes us to ambitious words, sentence patterns and new ideas.

5. Point 3 — Focus:
Reading trains concentration in a world full of distractions.

6. Close:
"So when we say reading matters, we are not just talking about books. We are talking about becoming more thoughtful, more articulate and more human."

Delivery choices:
- Pause after the opening sentence.
- Slow down before each new point.
- Look up at the audience during the final sentence.
- Use cue cards, not a full script.
`
      },

      workedExamples: [
        tier(
          'Developing',
          'Today I am going to talk about reading. Reading is important because it helps you learn things and it can be fun.',
          'This is clear, but it is predictable. It needs a stronger hook and more specific ideas.'
        ),
        tier(
          'Secure',
          'Reading matters because it gives us knowledge, imagination and empathy. It helps us understand people we may never meet and places we may never visit.',
          'This is more focused. It uses a list of three and develops a clearer central idea.'
        ),
        tier(
          'Grade 9 Ready',
          'If you only ever see the world through your own eyes, your world stays small. Reading changes that. It lets us borrow other lives, test other opinions and return to our own world with sharper minds.',
          'This is sophisticated. It uses contrast, metaphor and controlled phrasing to create a memorable opening.'
        )
      ],

      practice: [
        {
          type: 'mcq',
          question: 'Which is part of DELIVERY?',
          options: ['Eye contact', 'Paragraph indentation', 'Spelling accuracy'],
          correct: 0
        },
        {
          type: 'mcq',
          question: 'Which feature helps organise a speech?',
          options: ['Signposting phrases', 'Speaking as quickly as possible', 'Avoiding examples'],
          correct: 0
        },
        {
          type: 'true-false',
          question: 'A spoken task can include follow-up questions.',
          correct: true
        },
        {
          type: 'true-false',
          question: 'A good speaker should ignore the audience completely.',
          correct: false
        },
        {
          type: 'mcq',
          question: 'Which is the strongest opening?',
          options: [
            'Today I am going to talk about recycling.',
            'Every minute, we throw away choices that could have protected our future.',
            'My speech is about recycling and I hope you like it.'
          ],
          correct: 1
        }
      ],

      applicationTask: {
        title: 'Application Task',
        prompt: `Plan the first 60 seconds of a speech on a topic you care about. Include a hook, your main argument and a signpost to your first point.`,
        sentenceStarter: 'Imagine if...',
        hints: [
          'Open with a question, image, fact or short anecdote.',
          'State your main argument clearly.',
          'Use a signpost such as "The first reason is..."'
        ]
      },

      aiFeedback: {
        successCriteria: [
          'Opens with a clear hook',
          'States a focused main argument',
          'Uses Standard English appropriate to a formal task',
          'Includes at least one signposting phrase',
          'Shows awareness of audience'
        ]
      },

      mastery: {
        title: 'Mastery Challenge',
        prompt: `Why is spoken language assessed on both WHAT you say and HOW you say it? Explain using the terms content, organisation, language and delivery.`,
        xpReward: 50
      }
    }),

    // =========================================================
    // TOPIC 2 — SPEECHES AND PRESENTATIONS
    // =========================================================
    makeTopic({
      id: 'eng-6-2',
      slug: 'speeches-and-presentations',
      title: 'Speeches and Presentations',
      summary: 'Structure, technique and delivery for persuasive formal speaking.',

      explainer: {
        title: 'Speeches and Presentations',
        content: `
A strong speech or presentation combines structure, persuasive technique and controlled delivery.

A simple high-impact structure is:

1. HOOK
Grab attention quickly.
You could use:
- A rhetorical question
- A surprising fact
- A short story
- A vivid image
- A bold statement

2. THESIS
State your main argument clearly.
Example:
"I believe every school should protect at least 30 minutes of reading time each week."

3. BODY
Develop 2–4 clear points.
Each point should include:
- A topic sentence
- Explanation
- Example, evidence or detail
- Link back to the main argument

4. COUNTERPOINT
For higher-level speaking, briefly acknowledge another view.
Example:
"Some people argue that the timetable is already too full. However..."

5. CLOSE
End with impact.
You could use:
- A final challenge
- A repeated phrase
- A call to action
- A memorable image

Persuasive techniques:

A FOREST is a useful memory aid:
- A — Alliteration
- F — Facts
- O — Opinion
- R — Rhetorical question
- E — Emotive language
- S — Statistics
- T — Triples / list of three

Other useful techniques:
- Direct address: "you", "we", "our"
- Anecdote: a short personal story
- Contrast: "not because..., but because..."
- Repetition: repeating a key phrase
- Imperatives: "Imagine", "Consider", "Act"

Delivery matters because the audience hears the speech before they analyse it.

Strong delivery includes:
- Clear articulation
- Steady pace
- Planned pauses
- Varied tone
- Confident posture
- Natural eye contact
- Controlled gesture

Avoid:
- Reading every word from a script
- Speaking too fast
- Apologising at the start
- Looking at the floor
- Ending with "yeah, that's it"

Cue cards are usually better than a full script because they help you remember the structure while still allowing eye contact and natural delivery.
`
      },

      interactiveLesson: {
        title: 'Speeches',
        steps: [
          {
            type: 'question',
            question: 'The first 20 seconds of a speech should usually:',
            options: ['Hook the audience', 'Apologise for being nervous', 'List every paragraph'],
            correct: 0
          },
          {
            type: 'question',
            question: 'Which technique is a rhetorical question?',
            options: ['How many chances will we waste?', 'Safer streets, stronger schools', 'One, two, three'],
            correct: 0
          },
          {
            type: 'question',
            question: 'Which is usually better than reading from a full script?',
            options: ['Cue cards with key points', 'No preparation', 'Looking only at the slides'],
            correct: 0
          }
        ]
      },

      workedExample: {
        title: 'Worked Example — Powerful Opening',
        content: `
Task: Write an opening for a speech arguing that students should have more access to outdoor learning.

Weak opening:
"Today I am going to talk about outdoor learning. I think it is good and schools should do more of it."

Improved opening:
"When was the last time a lesson made you look up, breathe deeply and actually remember what you learned? For too many students, education happens under strip lights, behind desks and inside four walls. Outdoor learning would not replace the classroom — it would make the classroom stronger."

Why it works:
- Rhetorical question hooks the audience.
- Emotive contrast: "strip lights", "behind desks", "inside four walls".
- Clear argument: outdoor learning strengthens education.
- Confident tone.
- The final sentence uses contrast: "not replace... strengthen".

Delivery:
- Pause after the question.
- Slow down on "strip lights, behind desks and inside four walls".
- Stress "stronger" at the end.
`
      },

      workedExamples: [
        tier(
          'Developing',
          'School lunches should be healthier because students need good food. Unhealthy food is bad for you.',
          'The point is clear but general. It needs a hook, development and more persuasive language.'
        ),
        tier(
          'Secure',
          'If we expect students to concentrate, behave well and work hard, then we must give them food that helps them do exactly that. Healthy lunches are not a luxury; they are part of learning.',
          'This is persuasive because it links the issue to school success and uses direct logical reasoning.'
        ),
        tier(
          'Grade 9 Ready',
          'We would never expect a phone to run all day on 2% battery, yet we expect students to learn, focus and perform after eating food that gives them almost nothing useful. Healthy lunches are not a side issue. They are fuel for learning.',
          'This uses analogy, contrast and a short final sentence for impact.'
        )
      ],

      practice: [
        {
          type: 'mcq',
          question: '"Safer streets, stronger schools" is an example of:',
          options: ['Alliteration', 'A statistic', 'A counterargument'],
          correct: 0
        },
        {
          type: 'mcq',
          question: 'Which is a list of three?',
          options: ['Clear, fair and achievable', 'Very important', 'In my opinion'],
          correct: 0
        },
        {
          type: 'true-false',
          question: 'Pausing after a key point can make it more powerful.',
          correct: true
        },
        {
          type: 'true-false',
          question: 'A persuasive speech should never consider opposing views.',
          correct: false
        },
        {
          type: 'mcq',
          question: 'Which phrase directly addresses the audience?',
          options: ['You have the power to change this', 'The issue exists', 'Several people agree'],
          correct: 0
        }
      ],

      applicationTask: {
        title: 'Application Task',
        prompt: `Write a 30-second opening for a persuasive speech on one issue you care about. Use at least two persuasive techniques.`,
        sentenceStarter: 'How many times have we...',
        hints: [
          'Use a rhetorical question or vivid image.',
          'Use direct address: you, we, our.',
          'Try a list of three or contrast sentence.'
        ]
      },

      aiFeedback: {
        successCriteria: [
          'Creates an immediate hook',
          'Makes the topic and viewpoint clear',
          'Uses at least two persuasive techniques',
          'Uses controlled formal language',
          'Includes a delivery choice such as pause, emphasis or eye contact'
        ]
      },

      mastery: {
        title: 'Mastery Challenge',
        prompt: `Why can a quiet pause after a key sentence be more persuasive than shouting? Explain using audience, emphasis and control.`,
        xpReward: 50
      }
    }),

    // =========================================================
    // TOPIC 3 — DISCUSSION AND DEBATE
    // =========================================================
    makeTopic({
      id: 'eng-6-3',
      slug: 'discussion-and-debate',
      title: 'Discussion and Debate',
      summary: 'Learn how to build on others’ ideas, challenge courteously and argue clearly.',

      explainer: {
        title: 'Discussion and Debate',
        content: `
Discussion and debate test a different kind of speaking skill: not just presenting your own ideas, but listening, responding and developing a shared conversation.

DISCUSSION
A discussion explores a topic.
The aim is often to understand different viewpoints and build ideas together.

DEBATE
A debate argues for or against a clear motion.
The aim is to persuade, challenge and defend a position.

Strong speakers in discussion and debate can:
- Make a clear point.
- Support it with a reason or example.
- Listen to what others say.
- Build on useful ideas.
- Challenge politely.
- Ask clarifying questions.
- Summarise the discussion.
- Bring quieter speakers into the conversation.

Useful phrases for building on others:
- "I agree with part of that because..."
- "Building on that point..."
- "Another example would be..."
- "That links to..."
- "I would add that..."

Useful phrases for challenging courteously:
- "I understand that view, but..."
- "A possible weakness in that argument is..."
- "Could we also consider..."
- "Is there evidence for that?"
- "That may be true in some cases, however..."

Useful phrases for clarifying:
- "Do you mean that...?"
- "Could you explain what you mean by...?"
- "Can I check I have understood your point?"

A strong debate contribution often follows this structure:

POINT:
"I believe homework should be reduced."

REASON:
"This is because students already spend long hours in school."

EVIDENCE/EXAMPLE:
"For example, many students also have revision, clubs, family responsibilities and travel time."

LINK:
"Therefore, reducing homework could improve wellbeing without lowering standards."

In group speaking, balance is important.
Do not dominate. Do not disappear.
The best speakers contribute clearly, listen actively and help the conversation move forward.
`
      },

      interactiveLesson: {
        title: 'Discussion and Debate',
        steps: [
          {
            type: 'question',
            question: 'What is the main difference between discussion and debate?',
            options: [
              'Discussion explores ideas; debate argues for or against a motion',
              'Discussion is always silent; debate is always shouted',
              'Debate has no structure'
            ],
            correct: 0
          },
          {
            type: 'question',
            question: 'Which phrase challenges courteously?',
            options: [
              'I understand that view, but...',
              'That is stupid',
              'Nobody should listen to you'
            ],
            correct: 0
          },
          {
            type: 'question',
            question: 'What should you do if another speaker makes a useful point?',
            options: [
              'Build on it or respond to it',
              'Ignore it completely',
              'Talk over them'
            ],
            correct: 0
          }
        ]
      },

      workedExample: {
        title: 'Worked Example — Debate Contribution',
        content: `
Motion: "School uniforms should be abolished."

Weak contribution:
"I don't like uniform. It is annoying and people should wear what they want."

Improved contribution:
"I understand why some people support uniform, because it can create a sense of equality. However, I believe uniforms should be reviewed rather than simply defended. Many students find them uncomfortable, expensive and too rigid for different weather conditions. A better solution would be a simpler dress code that keeps standards high but gives families more flexibility."

Why it works:
- It acknowledges the opposing view.
- It avoids sounding rude or emotional.
- It gives clear reasons.
- It offers a practical alternative.
- It uses formal discussion language.

Strong debate speakers do not just disagree. They explain why, support their view and sound fair-minded.
`
      },

      workedExamples: [
        tier(
          'Developing',
          'I disagree because I just think phones should be allowed.',
          'This gives an opinion but no developed reason or evidence.'
        ),
        tier(
          'Secure',
          'I disagree with a total phone ban because phones can be useful for safety when travelling to and from school.',
          'This is clearer because it gives a specific reason.'
        ),
        tier(
          'Grade 9 Ready',
          'I understand the argument that phones can distract students, but a total ban may create new problems. A more balanced policy would allow phones to be switched off during lessons but available at the end of the day for travel, safety and communication with parents.',
          'This is stronger because it acknowledges the opposing argument and proposes a balanced solution.'
        )
      ],

      practice: [
        {
          type: 'mcq',
          question: 'Which phrase builds on someone else’s idea?',
          options: ['Building on that point...', 'That is irrelevant', 'I refuse to listen'],
          correct: 0
        },
        {
          type: 'mcq',
          question: 'Which is the best debate structure?',
          options: ['Point, reason, evidence/example, link', 'Shout, repeat, interrupt, stop', 'Opinion only'],
          correct: 0
        },
        {
          type: 'true-false',
          question: 'In discussion, listening is part of being assessed.',
          correct: true
        },
        {
          type: 'true-false',
          question: 'A strong speaker should dominate every group discussion.',
          correct: false
        },
        {
          type: 'mcq',
          question: 'Which is a clarifying question?',
          options: ['Could you explain what you mean by that?', 'You are wrong', 'Can I leave now?'],
          correct: 0
        }
      ],

      applicationTask: {
        title: 'Application Task',
        prompt: `Write a short debate contribution for or against this motion: "Students should have a four-day school week." Include a point, reason, example and link back to the motion.`,
        sentenceStarter: 'I understand why some people might argue that..., however...',
        hints: [
          'Acknowledge one opposing view.',
          'Use at least one reason.',
          'Offer a realistic example or consequence.'
        ]
      },

      aiFeedback: {
        successCriteria: [
          'States a clear viewpoint',
          'Uses a reason and example',
          'Responds to or acknowledges another viewpoint',
          'Uses courteous debate language',
          'Links back to the motion'
        ]
      },

      mastery: {
        title: 'Mastery Challenge',
        prompt: `Why is courteous disagreement more powerful than aggressive disagreement in a formal debate?`,
        xpReward: 50
      }
    }),

    // =========================================================
    // TOPIC 4 — PERFORMING PLAYS AND POEMS
    // =========================================================
    makeTopic({
      id: 'eng-6-4',
      slug: 'performing-plays-and-poems',
      title: 'Performing Plays and Poems',
      summary: 'Use voice, movement and interpretation to bring texts to life.',

      explainer: {
        title: 'Performing Plays and Poems',
        content: `
Performing a play or poem means turning written language into spoken meaning.

You are not just reading words aloud.
You are making interpretation choices.

For poems, consider:

PUNCTUATION
- Commas usually suggest a short pause.
- Full stops usually suggest a longer pause.
- Dashes may suggest interruption, hesitation or a shift in thought.
- Question marks change intonation.

LINE BREAKS
A line break may create:
- A small pause
- Suspense
- Emphasis
- A change in rhythm

RHYTHM
Poems often have patterns of sound.
You can emphasise rhythm by noticing:
- Stressed syllables
- Repeated sounds
- Repeated phrases
- Changes in pace

TONE AND MOOD
Your voice should match the meaning.
For example:
- Grief may sound slower, quieter and heavier.
- Anger may sound sharper and faster.
- Fear may include hesitation or breathiness.
- Joy may sound lighter and more energetic.

For plays, consider:

CHARACTER
Ask:
- What does the character want?
- What are they feeling?
- What are they hiding?
- What is their status compared with other characters?

VOICE
You can control:
- Volume
- Pace
- Pitch
- Tone
- Emphasis
- Silence

BODY LANGUAGE
You can use:
- Posture
- Gesture
- Stillness
- Movement
- Facial expression
- Distance from other characters

LISTENING
In drama, listening matters.
A scene should feel like a real exchange, not separate people waiting to say their lines.

INTERPRETATION
Two excellent performers may deliver the same line differently because they interpret the character differently.

Example:
Line: "I am not afraid."

Possible performance 1:
Said loudly, with direct eye contact. This suggests courage.

Possible performance 2:
Said quietly, with shaking hands. This suggests the character is trying to convince themselves.

Both can work if the performance choice is justified by the text.
`
      },

      interactiveLesson: {
        title: 'Performing Texts',
        steps: [
          {
            type: 'question',
            question: 'A dash in a poem or script may suggest:',
            options: ['A pause, interruption or change in thought', 'A spelling error', 'That the line should be ignored'],
            correct: 0
          },
          {
            type: 'question',
            question: 'Body language in drama can show:',
            options: ['Emotion and status', 'The number of pages', 'The spelling of a word'],
            correct: 0
          },
          {
            type: 'question',
            question: 'Why can two actors perform the same line differently?',
            options: ['They may have different interpretations', 'Only one actor has read it', 'The line has no meaning'],
            correct: 0
          }
        ]
      },

      workedExample: {
        title: 'Worked Example — Performance Notes',
        content: `
Line:
"To be, or not to be — that is the question."

Possible performance notes:
- Pause briefly after "To be," to suggest thought.
- Slow down on "or not to be" to show uncertainty.
- Use a longer pause at the dash to show the weight of the decision.
- Stress "question" because it is the key idea.
- Keep body still to suggest internal conflict rather than external action.

Why this works:
The line becomes a moment of thinking, not just a famous quotation. The pauses and stillness help the audience sense hesitation and seriousness.
`
      },

      workedExamples: [
        tier(
          'Developing',
          'I would say the line loudly because it is important.',
          'This identifies volume but does not explain mood, meaning or character.'
        ),
        tier(
          'Secure',
          'I would start quietly and pause after the comma to show the character is thinking carefully.',
          'This links voice and pause to interpretation.'
        ),
        tier(
          'Grade 9 Ready',
          'I would begin almost conversationally, then slow down before the dash, allowing the silence to suggest the character is confronting an idea they cannot easily say aloud.',
          'This is sophisticated because it links pace, silence and interpretation to character psychology.'
        )
      ],

      practice: [
        {
          type: 'mcq',
          question: 'A line break in a poem often signals:',
          options: ['A small pause or emphasis', 'A spelling mistake', 'A new chapter'],
          correct: 0
        },
        {
          type: 'mcq',
          question: 'Which is a voice choice?',
          options: ['Volume', 'Costume price', 'Page number'],
          correct: 0
        },
        {
          type: 'true-false',
          question: 'Stillness can be as powerful as movement in performance.',
          correct: true
        },
        {
          type: 'true-false',
          question: 'A performer should ignore punctuation completely.',
          correct: false
        },
        {
          type: 'mcq',
          question: 'Which performance choice best suggests fear?',
          options: ['Hesitant pace and quieter voice', 'Flat voice with no pauses', 'Laughing loudly throughout'],
          correct: 0
        }
      ],

      applicationTask: {
        title: 'Application Task',
        prompt: `Choose any short line from a poem or play. Write three performance notes explaining how you would use voice, pause and body language.`,
        sentenceStarter: 'Line: ___\nVoice:...\nPause:...\nBody language:...',
        hints: [
          'Name the mood you want to create.',
          'Mention one specific word you would stress.',
          'Explain what your body language shows.'
        ]
      },

      aiFeedback: {
        successCriteria: [
          'Identifies a specific line',
          'Explains a voice choice',
          'Explains a pause or emphasis choice',
          'Explains body language or movement',
          'Links performance choices to mood, character or meaning'
        ]
      },

      mastery: {
        title: 'Mastery Challenge',
        prompt: `Why is performance a form of interpretation rather than simply reading aloud?`,
        xpReward: 50
      }
    }),

    // =========================================================
    // TOPIC 5 — USING LANGUAGE CORRECTLY
    // =========================================================
    makeTopic({
      id: 'eng-6-5',
      slug: 'using-language-correctly',
      title: 'Using Language Correctly',
      summary: 'Match language, register and vocabulary to audience and purpose.',

      explainer: {
        title: 'Using Language Correctly',
        content: `
Effective speakers adapt their language to audience, purpose and context.

AUDIENCE
Who are you speaking to?
- Teacher
- Examiner
- Classmates
- Younger students
- Parents
- Interview panel
- Public audience

PURPOSE
Why are you speaking?
- To inform
- To persuade
- To argue
- To explain
- To entertain
- To perform
- To discuss

CONTEXT
How formal is the situation?
- Classroom discussion may be semi-formal.
- A GCSE presentation is formal.
- A debate is formal and structured.
- A conversation with friends is informal.

REGISTER
Register means the level of formality in your language.

Formal register:
- "I would like to argue that..."
- "This issue deserves serious attention."
- "A possible solution would be..."

Informal register:
- "I reckon..."
- "It's kind of annoying..."
- "Loads of people think..."

In assessed formal speaking, use Standard English where appropriate.

Avoid:
- Repeated filler words: "um", "er", "like", "basically", "you know"
- Slang in formal tasks
- Mumbling
- Overly casual phrasing
- Starting sentences and not finishing them
- Reading mechanically from a script

Useful bridging phrases:
- "Let me put that another way..."
- "The key idea here is..."
- "What I mean by that is..."
- "A useful example would be..."
- "The most important point is..."
- "To return to my main argument..."

These phrases help you recover if you lose your place. They also make you sound controlled.

Improving accuracy:
- Speak in complete sentences.
- Replace vague words with precise ones.
- Use connectives to link ideas.
- Use subject terminology where relevant.
- Practise difficult words aloud before the task.

Examples of precise vocabulary:
- Instead of "good", use "effective", "valuable", "convincing", "beneficial".
- Instead of "bad", use "harmful", "ineffective", "damaging", "unconvincing".
- Instead of "a lot", use "significant", "considerable", "substantial".

The aim is not to sound unnatural. The aim is to sound clear, controlled and appropriate.
`
      },

      interactiveLesson: {
        title: 'Language Control',
        steps: [
          {
            type: 'question',
            question: 'Register means:',
            options: ['The level of formality in language', 'The speed of handwriting', 'The number of slides used'],
            correct: 0
          },
          {
            type: 'question',
            question: 'Which phrase is most formal?',
            options: ['I would like to argue that...', 'Yeah, so basically...', 'It is dead annoying'],
            correct: 0
          },
          {
            type: 'question',
            question: 'A bridging phrase helps you:',
            options: ['Recover smoothly and keep speaking clearly', 'Avoid answering questions', 'Sound more confused'],
            correct: 0
          }
        ]
      },

      workedExample: {
        title: 'Worked Example — Formalising Speech',
        content: `
Informal version:
"Yeah so basically, like, we wanna change the canteen food because it's not great and loads of people are annoyed."

Improved formal version:
"We would like to propose changes to the canteen menu because many students feel the current options are limited, expensive and not always healthy."

Why it is better:
- Removes fillers: "yeah", "basically", "like".
- Replaces slang: "wanna" becomes "would like to".
- Replaces vague language: "not great" becomes "limited, expensive and not always healthy".
- Uses a clear, polite formal tone.
- Keeps the message focused.
`
      },

      workedExamples: [
        tier(
          'Developing',
          'I think phones are bad because people are always on them and it is annoying.',
          'This is understandable but vague. It needs more precise vocabulary and a more formal register.'
        ),
        tier(
          'Secure',
          'I believe excessive phone use can damage concentration because students become distracted from learning and face-to-face communication.',
          'This is clearer and more formal. It uses precise vocabulary such as "excessive", "damage" and "concentration".'
        ),
        tier(
          'Grade 9 Ready',
          'While phones can be useful tools, uncontrolled use can weaken concentration, reduce meaningful conversation and make it harder for students to remain fully present in lessons.',
          'This is balanced, precise and controlled. It uses contrast and a list of three.'
        )
      ],

      practice: [
        {
          type: 'mcq',
          question: 'Which is filler language?',
          options: ['You know', 'Therefore', 'Furthermore'],
          correct: 0
        },
        {
          type: 'mcq',
          question: 'Which word is more precise than "bad"?',
          options: ['Harmful', 'Thingy', 'Stuff'],
          correct: 0
        },
        {
          type: 'true-false',
          question: 'Formal assessed speaking usually requires controlled Standard English.',
          correct: true
        },
        {
          type: 'true-false',
          question: 'Using fillers repeatedly can make a speaker sound less confident.',
          correct: true
        },
        {
          type: 'mcq',
          question: 'Which is the best bridging phrase?',
          options: ['Let me put that another way...', 'Whatever...', 'You get me?'],
          correct: 0
        }
      ],

      applicationTask: {
        title: 'Application Task',
        prompt: `Rewrite this informal spoken line for a formal audience: "Yeah so basically, like, we wanna change the canteen food innit?"`,
        sentenceStarter: 'We would like to propose...',
        hints: [
          'Remove filler words.',
          'Use Standard English.',
          'Make the request polite and specific.'
        ]
      },

      aiFeedback: {
        successCriteria: [
          'Removes filler language',
          'Uses Standard English',
          'Uses formal register',
          'Keeps the meaning clear',
          'Uses precise vocabulary'
        ]
      },

      mastery: {
        title: 'Mastery Challenge',
        prompt: `Why can removing only a few filler words dramatically improve how confident and credible a speaker sounds?`,
        xpReward: 50
      }
    }),

    // =========================================================
    // TOPIC 6 — ANSWERING QUESTIONS AND THINKING ON YOUR FEET
    // =========================================================
    makeTopic({
      id: 'eng-6-6',
      slug: 'answering-questions',
      title: 'Answering Questions',
      summary: 'Respond clearly, calmly and thoughtfully to follow-up questions.',

      explainer: {
        title: 'Answering Questions',
        content: `
Many spoken language tasks include follow-up questions.

This is often where students worry most, because the answers are not fully scripted.

The key is not to give a perfect answer instantly.
The key is to listen, think and respond clearly.

A strong answer usually does four things:

1. LISTEN
Make sure you understand the question.
Do not start answering before the question has finished.

2. CLARIFY
If needed, ask for the question to be repeated or clarified.
Useful phrases:
- "Could you repeat the question, please?"
- "Do you mean...?"
- "Can I check that you are asking about...?"

3. ANSWER DIRECTLY
Start with a clear answer.
Example:
"Yes, I think schools should do more to support mental health."

4. DEVELOP
Add a reason, example or qualification.
Example:
"This is because students are more likely to succeed academically when they feel safe, supported and able to ask for help."

Useful structure:
ANSWER — REASON — EXAMPLE — LINK

Example:
Question:
"Do you think your idea would be expensive?"

Answer:
"It could involve some cost, but I think it would be manageable. For example, schools could begin with low-cost changes such as peer mentoring, clearer signposting and staff training. Therefore, cost should be considered, but it should not stop improvement altogether."

If you do not know the answer:
Do not panic.
Use a controlled phrase:
- "That is a difficult question, but my initial view is..."
- "I have not considered that fully, but I would suggest..."
- "I would need more evidence to answer completely, but..."

This sounds much better than:
- "I don't know."
- "No idea."
- "Erm... yeah... maybe."

Strong question responses show maturity because they prove you can think beyond your prepared speech.
`
      },

      interactiveLesson: {
        title: 'Answering Questions',
        steps: [
          {
            type: 'question',
            question: 'What should you do before answering a follow-up question?',
            options: ['Listen carefully to the whole question', 'Interrupt immediately', 'Look away and ignore it'],
            correct: 0
          },
          {
            type: 'question',
            question: 'Which is a good clarification phrase?',
            options: ['Could you repeat the question, please?', 'I was not listening', 'That question is bad'],
            correct: 0
          },
          {
            type: 'question',
            question: 'A strong answer can use the structure:',
            options: ['Answer, reason, example, link', 'Panic, guess, stop', 'Repeat, whisper, shrug'],
            correct: 0
          }
        ]
      },

      workedExample: {
        title: 'Worked Example — Follow-up Question',
        content: `
Speech topic:
"Schools should start later in the morning."

Follow-up question:
"Would a later start time create problems for parents?"

Weak answer:
"Maybe. I don't know. It depends."

Improved answer:
"Yes, it could create problems for some parents, especially those who start work early. However, schools could reduce this problem by offering supervised breakfast clubs or a phased start. So while the issue is real, it does not necessarily mean the whole idea should be rejected."

Why it works:
- It answers the question directly.
- It acknowledges a genuine problem.
- It offers a practical solution.
- It returns to the main argument.
- It sounds thoughtful rather than defensive.
`
      },

      workedExamples: [
        tier(
          'Developing',
          'I do not know, maybe it would work.',
          'This is honest but underdeveloped. It needs a reason or example.'
        ),
        tier(
          'Secure',
          'I think it could work if schools introduced it gradually, because sudden changes are harder for families to manage.',
          'This gives a clear answer and reason.'
        ),
        tier(
          'Grade 9 Ready',
          'It would depend on how the change was introduced. A sudden change could create pressure for families, but a trial period, consultation and optional early supervision would make the idea much more realistic.',
          'This is balanced and mature. It considers conditions, consequences and solutions.'
        )
      ],

      practice: [
        {
          type: 'mcq',
          question: 'Which is the best first response if you do not understand a question?',
          options: ['Could you repeat the question, please?', 'I refuse to answer', 'That makes no sense'],
          correct: 0
        },
        {
          type: 'mcq',
          question: 'Which answer sounds most thoughtful?',
          options: [
            'That is a difficult question, but my initial view is...',
            'Dunno.',
            'Whatever.'
          ],
          correct: 0
        },
        {
          type: 'true-false',
          question: 'Follow-up questions test whether you can think beyond your prepared script.',
          correct: true
        },
        {
          type: 'true-false',
          question: 'You should always pretend you know an answer, even if you do not.',
          correct: false
        },
        {
          type: 'mcq',
          question: 'Which part develops an answer?',
          options: ['A reason or example', 'A shrug', 'Changing the subject'],
          correct: 0
        }
      ],

      applicationTask: {
        title: 'Application Task',
        prompt: `Imagine you gave a speech arguing that homework should be reduced. Answer this follow-up question: "Would reducing homework lower academic standards?"`,
        sentenceStarter: 'Not necessarily, because...',
        hints: [
          'Answer the question directly.',
          'Acknowledge the concern.',
          'Offer a balanced reason or solution.'
        ]
      },

      aiFeedback: {
        successCriteria: [
          'Answers the question directly',
          'Gives a reason',
          'Uses an example or solution',
          'Sounds calm and thoughtful',
          'Avoids vague filler'
        ]
      },

      mastery: {
        title: 'Mastery Challenge',
        prompt: `Why is a balanced answer often stronger than a one-sided answer when responding to follow-up questions?`,
        xpReward: 50
      }
    }),

    // =========================================================
    // TOPIC 7 — SPOKEN LANGUAGE KNOWLEDGE ORGANISER
    // =========================================================
    makeTopic({
      id: 'eng-6-7',
      slug: 'spoken-language-knowledge-organiser',
      title: 'Spoken Language — Knowledge Organiser',
      summary: 'Quick-reference summary for Section 6.',

      explainer: {
        title: 'Knowledge Organiser',
        content: `
Use this organiser to revise Section 6.

KEY ASSESSMENT AREAS
- Content: what you say.
- Organisation: how your ideas are structured.
- Language: vocabulary, register and Standard English.
- Delivery: voice, pace, pause, tone, volume, eye contact and body language.
- Response: listening, answering questions and building on others' ideas.

SPEECH STRUCTURE
1. Hook
2. Thesis / main argument
3. 2–4 developed points
4. Counterpoint or alternative view
5. Memorable close

PERSUASIVE TECHNIQUES — A FOREST
- Alliteration
- Facts
- Opinion
- Rhetorical question
- Emotive language
- Statistics
- Triples / list of three

DISCUSSION AND DEBATE
Strong discussion speakers:
- Listen actively.
- Build on others' ideas.
- Challenge courteously.
- Ask clarifying questions.
- Keep the discussion focused.
- Summarise when useful.

Useful phrases:
- "Building on that point..."
- "I understand that view, but..."
- "Could we also consider..."
- "A possible weakness in that argument is..."
- "To summarise..."

PERFORMANCE
For poems:
- Notice punctuation.
- Use line breaks.
- Control rhythm.
- Match tone to mood.

For plays:
- Understand character.
- Use voice and body language.
- Listen to other actors.
- Use movement, stillness, silence and action for impact.

LANGUAGE CONTROL
Use formal register when appropriate.
Avoid:
- Slang in formal tasks
- Repeated fillers
- Mumbling
- Unfinished sentences
- Mechanical reading

Use bridging phrases:
- "Let me put that another way..."
- "The key idea here is..."
- "A useful example would be..."
- "To return to my main argument..."

FOLLOW-UP QUESTIONS
Use:
ANSWER — REASON — EXAMPLE — LINK

Example:
"Yes, I partly agree because... For example... Therefore..."

FINAL CHECKLIST
Before speaking, ask:
- Do I know my main argument?
- Have I planned a strong opening?
- Can I explain each point clearly?
- Have I rehearsed aloud?
- Have I practised eye contact and pauses?
- Can I answer likely questions?
- Does my language match the audience and purpose?
`
      },

      interactiveLesson: {
        title: 'Recall Quiz',
        steps: [
          {
            type: 'question',
            question: 'Which is NOT part of speech structure?',
            options: ['Random fillers', 'Hook', 'Close'],
            correct: 0
          },
          {
            type: 'question',
            question: 'A FOREST is a memory aid for:',
            options: ['Persuasive techniques', 'Poetry line numbers', 'Shakespeare characters'],
            correct: 0
          },
          {
            type: 'question',
            question: 'Which phrase challenges courteously?',
            options: ['I understand that view, but...', 'That is stupid', 'Stop talking'],
            correct: 0
          },
          {
            type: 'question',
            question: 'Which improves spoken fluency fastest?',
            options: ['Rehearsing aloud', 'Never practising', 'Reading silently once'],
            correct: 0
          }
        ]
      },

      workedExample: {
        title: 'Worked Summary — From Weak to Strong',
        content: `
Weak opening:
"Today I am going to talk about kindness. Kindness is good and people should be kind."

Improved opening:
"If kindness costs nothing, why do we sometimes act as if we cannot afford it? In schools, online and in everyday life, small acts of kindness can change the atmosphere around us."

What this uses:
- Rhetorical question
- Contrast: "costs nothing" / "cannot afford it"
- List of three: "in schools, online and in everyday life"
- Clear main idea
- Formal, controlled tone

Delivery:
- Pause after the question.
- Emphasise "nothing" and "cannot afford it".
- Look up during the second sentence.
`
      },

      workedExamples: [
        tier(
          'Developing',
          'My speech is about kindness and I think kindness is nice.',
          'This is understandable but too general and repetitive.'
        ),
        tier(
          'Secure',
          'Kindness matters because it can make school feel safer, friendlier and more supportive.',
          'This is clearer and uses a list of three.'
        ),
        tier(
          'Grade 9 Ready',
          'Kindness is often treated as something small, but small things can change the climate of a classroom, the confidence of a student and the character of a community.',
          'This is more ambitious. It uses contrast, repetition and a developed list of three.'
        )
      ],

      practice: [
        {
          type: 'mcq',
          question: 'Which is part of delivery?',
          options: ['Pace', 'Spelling', 'Paragraph length'],
          correct: 0
        },
        {
          type: 'mcq',
          question: 'Which is a bridging phrase?',
          options: ['Let me put that another way...', 'You know, like...', 'Whatever'],
          correct: 0
        },
        {
          type: 'mcq',
          question: 'Which is useful in debate?',
          options: ['Courteous challenge', 'Interrupting aggressively', 'Ignoring evidence'],
          correct: 0
        },
        {
          type: 'true-false',
          question: 'Performance choices should link to mood, meaning or character.',
          correct: true
        },
        {
          type: 'true-false',
          question: 'Formal spoken language should always use slang.',
          correct: false
        }
      ],

      applicationTask: {
        title: 'Application Task',
        prompt: `Pick three ideas from Section 6 and explain how each will improve your next spoken task.`,
        sentenceStarter: 'Firstly, I will improve my...',
        hints: [
          'Choose ideas from different areas: structure, language, delivery, discussion or answering questions.',
          'Explain how each idea will make your speaking clearer or more persuasive.',
          'Include one action you will practise.'
        ]
      },

      aiFeedback: {
        successCriteria: [
          'Chooses three distinct spoken language skills',
          'Explains how each skill improves performance',
          'Uses accurate terminology',
          'Includes a practical improvement plan',
          'Writes in clear Standard English'
        ]
      },

      mastery: {
        title: 'Mastery Challenge',
        prompt: `Which matters more in spoken language: what you say, how you organise it, or how you deliver it? Give a balanced answer.`,
        xpReward: 50
      }
    })
  ]
};

export const getEnglishContent6TopicCount = () => englishContent6.topics.length;

export default englishContent6;